/**
 * Translate raw backend error messages (English) into the user's language.
 *
 * The backend still answers many errors with an English sentence (`detail` or `message`),
 * e.g. "Can only approve PENDING requisitions, current: APPROVED". Instead of changing
 * every endpoint, the frontend keeps a catalog of those sentences in
 * `public/locales/en/common.json` → `backendErrors.<key>` (English template, with
 * `{{name}}` for the variable parts) and their translation under the same key in the
 * other languages.
 *
 * `translateBackendMessage` turns each English template into a regex, finds the one that
 * matches the whole message, and returns `t('common:backendErrors.<key>', vars)`.
 * Messages that match no template are returned unchanged, so unknown or already
 * translated messages are never broken.
 *
 * To translate a new backend message: add the English sentence (same text as the
 * backend, variables as `{{name}}`) to en/common.json `backendErrors` and its
 * translation to es/common.json under the same key.
 */
import i18n from 'i18next';

let compiledFor = null;
let compiled = [];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function compile() {
  const templates = i18n.getResourceBundle?.('en', 'common')?.backendErrors;
  if (!templates) return [];
  if (compiledFor === templates) return compiled;
  compiled = Object.entries(templates)
    .map(([key, template]) => {
      const names = [];
      const body = template
        .trim()
        .replace(/\.$/, '')
        .split(/(\{\{\w+\}\})/)
        .map((part) => {
          const m = part.match(/^\{\{(\w+)\}\}$/);
          if (!m) return escapeRegExp(part);
          names.push(m[1]);
          return '(.+?)';
        })
        .join('');
      return {
        key,
        names,
        re: new RegExp(`^${body}\\.?$`, 's'),
        // Templates with more fixed text are more specific: try them first.
        weight: template.replace(/\{\{\w+\}\}/g, '').length,
      };
    })
    .sort((a, b) => b.weight - a.weight);
  compiledFor = templates;
  return compiled;
}

export function translateBackendMessage(message) {
  if (typeof message !== 'string' || !message.trim()) return message;
  const lng = i18n.resolvedLanguage || i18n.language || 'en';
  if (lng.startsWith('en')) return message;
  const text = message.trim();
  for (const { key, names, re } of compile()) {
    const m = text.match(re);
    if (m) {
      const vars = {};
      names.forEach((name, i) => { vars[name] = m[i + 1]; });
      return i18n.t(`common:backendErrors.${key}`, { ...vars, interpolation: { escapeValue: false } });
    }
  }
  return message;
}

export default translateBackendMessage;
